import { Product } from "../types/product";
import { AddOrUpdateProductReq } from "../request/addOrUpdateProductReq";
import axiosInstance, { axiosMultipartInstance } from "../config/axios";
import HttpService from "./httpService";
import { FileService } from "./fileService";

export interface AddOrUpdateProductData {
  data: any;
  // Optional 0–100 progress callback covering the image-upload phase.
  onProgress?: (percent: number) => void;
}

export class ProductService {
  static getStats = async (params?: Record<string, string>) => {
    return HttpService.get<{
      totalSales: number;
      totalOrders: number;
      totalProducts: number;
      totalCustomers: number;
      range?: { preset: string; from: string; to: string };
    }>(`/stats`, { params });
  };

  static getInventoryStats = async () => {
    return HttpService.get<{
      totalProducts: number;
      totalUnits: number;
      inventoryValue: number;
      lowStock: number;
      outOfStock: number;
    }>(`/products/inventory-stats`);
  };

  static getProductById = async (id?: string | null) => {
    //console.log("Here")
    try {
      const response = await axiosInstance.get<Product>(`/products/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  static deleteProductById = async (id?: string | null) => {
    try {
      const response = await axiosInstance.delete(`/products/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  static getAllProducts = async (): Promise<Product[]> => {
    console.log("getAllProducts is called");

    try {
      const response = await axiosInstance.get<Product[]>(`/products`);
      return response.data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  };

  static getAllProductsPaginated = async (
    page: number = 1,
    limit: number = 20,
    search?: string,
    sortBy?: string,
    sortOrder?: 'asc' | 'desc'
  ): Promise<{ products: Product[]; total: number; totalPages: number; currentPage: number }> => {
    console.log("getAllProductsPaginated is called with page:", page, "limit:", limit, "search:", search, "sortBy:", sortBy, "sortOrder:", sortOrder);

    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      if (search && search.trim()) {
        params.append('search', search.trim());
      }

      if (sortBy) {
        params.append('sortBy', sortBy);
      }

      if (sortOrder) {
        params.append('sortOrder', sortOrder);
      }

      const response = await axiosInstance.get<{
        data: Product[];
        meta: {
          total: number;
          page: number;
          limit: number;
          totalPages: number;
          hasNext: boolean;
          hasPrev: boolean;
        };
      }>(`/products?${params.toString()}`);

      return {
        products: response.data.data,
        total: response.data.meta.total,
        totalPages: response.data.meta.totalPages,
        currentPage: response.data.meta.page,
      };
    } catch (error) {
      console.log(error);
      throw error;
    }
  };

  static searchProducts = async (query: string): Promise<Product[]> => {
    try {
      const response = await axiosInstance.get<Product[]>(`/products/search?query=${encodeURIComponent(query)}`);
      return response.data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  };

  static addOrUpdateProduct = async ({ data, onProgress }: AddOrUpdateProductData): Promise<Product> => {
    try {
      // --- Overall upload progress across all batches (base + per-variant) ---
      // Each batch that has new Files to upload is weighted equally; within a
      // batch we use axios's byte-level progress. This keeps the bar smooth and
      // monotonic even though uploads happen in several sequential requests.
      const isFile = (img: unknown): img is File => typeof img !== "string";
      const baseFileCount = (data.images || []).filter(isFile).length;
      const variantBatchesWithFiles = (data.hasVariants && Array.isArray(data.variants))
        ? data.variants.filter((v: any) => (v.images || []).some(isFile)).length
        : 0;
      const totalBatches = (baseFileCount > 0 ? 1 : 0) + variantBatchesWithFiles;
      let completedBatches = 0;

      const reportBatchProgress = (loaded: number, total: number) => {
        if (!onProgress || totalBatches === 0) return;
        const fraction = total > 0 ? loaded / total : 0;
        const percent = Math.min(
          100,
          Math.round(((completedBatches + fraction) / totalBatches) * 100)
        );
        onProgress(percent);
      };

      // Helper function to process images (upload Files, keep URLs)
      const processImages = async (images: (string | File)[]): Promise<string[]> => {
        const urls: string[] = [];
        const files: File[] = [];

        // Separate URLs and Files
        images.forEach((img) => {
          if (typeof img === "string") {
            urls.push(img); // Keep existing URLs
          } else {
            files.push(img); // Files need to be uploaded
          }
        });

        // Upload new files if any
        if (files.length > 0) {
          const uploadedFiles = (await FileService.uploadFiles(files, reportBatchProgress)).files;
          completedBatches += 1; // this batch is fully uploaded
          reportBatchProgress(0, 0); // snap the bar to the completed-batch boundary
          urls.push(...uploadedFiles.map((f) => f.url));
        }

        return urls;
      };

      // Process base product images
      const baseImageUrls = await processImages(data.images || []);

      // Upload variant images if they exist
      if (data.hasVariants && data.variants) {
        console.log("=== SERVICE VARIANT DEBUG ===");
        console.log("Processing variants:", data.variants.length);
        data.variants.forEach((variant: any, index: number) => {
          console.log(`Variant ${index}:`, {
            attributes: variant.attributes,
            existingImages: variant.existingImages?.length || 0,
            newImages: variant.images?.length || 0,
            existingImagesData: variant.existingImages,
            newImagesData: variant.images,
          });
        });
        console.log("=== END SERVICE VARIANT DEBUG ===");

        // Process each variant's images
        for (const variant of data.variants) {
          // Upload new images
          const newImageUrls = await processImages(variant.images || []);

          // Combine existing URLs with new uploaded URLs
          const allImageUrls = [...(variant.existingImages || []), ...newImageUrls];

          // Variants with no images of their own inherit the main product photos.
          variant.images = allImageUrls.length > 0 ? allImageUrls : [...baseImageUrls];
          delete variant.existingImages; // Remove the separate field
        }
      }

      // All image uploads done — mark the upload phase complete.
      onProgress?.(100);

      // Prepare product data with processed image URLs
      const productData = {
        ...data,
        images: baseImageUrls,
        // Ensure ID is included for updates, or use empty string for new products
        id: data.id || "",
      } as AddOrUpdateProductReq;

      // Call the service to add or update the product
      return await HttpService.post<Product>(`/products`, productData);
    } catch (error) {
      throw error;
    }
  };
}

export const getAdminProduct = async (id: string) => {
  try {
    const response = await axiosInstance.get<Product>(`/admin/products/${id}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getAllProduct = async () => {
  try {
    const response = await axiosInstance.get<Product[]>(`/admin/product`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const addProduct = async (productReq: AddOrUpdateProductReq) => {
  try {
    return await HttpService.post<any>("/admin/product", productReq);
  } catch (error) {
    console.log(error);
    throw error;
  }
};
